import { useState, useMemo, useEffect, useEffectEvent, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, RefreshCw, Mail, UserCheck, Users, UserX, CheckSquare, Square, CloudDownload, UserPlus } from 'lucide-react'
import { ClientesFiltros, type FiltrosState } from '@/components/clientes/ClientesFiltros'
import { FILTROS_INICIALES } from '@/components/clientes/clientesFiltros.constants'
import { ClientesTable } from '@/components/clientes/ClientesTable'
import { ClientesBulkActionsBar } from '@/components/clientes/ClientesBulkActionsBar'
import { ClienteFormDialog } from '@/components/clientes/ClienteFormDialog'
import { ConfirmarBienvenidaDialog } from '@/components/clientes/ConfirmarBienvenidaDialog'
import { EstadoCuentaDialog } from '@/components/clientes/EstadoCuentaDialog'
import { ClienteDetalleDialog } from '@/components/clientes/ClienteDetalleDialog'
import { SincronizarPadronMasivoDialog } from '@/components/clientes/SincronizarPadronMasivoDialog'
import { MotivoBajaDialog } from '@/components/clientes/forms/MotivoBajaDialog'
import { TareaFormDialog, InscripcionFormDialog, type TareaFormValues } from '@/components/tareas'
import { useCrearTarea, tareasKeys } from '@/hooks/useTareas'
import { tareasService } from '@/core/services/tareasService'
import {
  useEtiquetasCliente,
  useEtiquetasDeCliente,
  useEtiquetasDeClientes,
  useEtiquetarEnLote,
  useQuitarEtiquetaEnLote,
  type ResultadoEtiquetaEnLote,
} from '@/core/hooks/useEtiquetasCliente'
import type { ClienteFormValues } from '@/components/clientes/wizard/clienteSchema'
import type { ContextoEdicionCliente } from '@/components/clientes/wizard/ClienteWizardNew'
import {
  useClientes,
  useCliente,
  useActualizarCliente,
  useEliminarCliente,
  useServiciosCliente,
  useAsignarResponsablesMasivo,
} from '@/hooks/useClientes'
import { useAltaCliente, armarClienteInput, serviciosConPrecioValido, useGuardarEtiquetasCliente } from '@/hooks/useAltaCliente'
import { useServicios, serviciosKeys } from '@/hooks/useServicios'
import { guardarServiciosCliente, type ResultadoGuardarServicios } from '@/services/serviciosClienteEdicion'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { useUltimosEnviosEstadoCuenta } from '@/hooks/useEmailLog'
import { usePermissions } from '@/hooks/usePermissions'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useSupabase } from '@/lib/supabase-context'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { generarContratoBase64, DatosEstudioIncompletosError } from '@/lib/contratoHelpers'
import { generarEstadoCuentaBase64 } from '@/lib/estadoCuentaBase64'
import type { VistaSaldoCliente } from '@/types/database'
import { clientesService, type VistaSaldoClienteConImpuestos, type ClienteCompletoInput } from '@/services/clientesService'
import type { MotivoBajaFormValues } from '@/components/clientes/forms/motivoBajaSchema'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { handleMutationError, puedeTocarTarea } from '@/lib/permisos-guard'
import { personasService } from '@/services/personasService'
import { formatearCUIT } from '@/lib/utils'
import { useVault } from '@/hooks/useVault'
import { puedeVerClaveInscripcion } from '@/core/lib/inscripcion'

type AccionEtiqueta = 'poner' | 'quitar'

/** Muestra el resultado de poner o quitar una etiqueta a varios clientes. */
const avisarEtiquetaEnLote = (
  accion: AccionEtiqueta,
  nombre: string,
  { hechos, fallos, sinCambio }: ResultadoEtiquetaEnLote
) => {
  if (fallos > 0) {
    toast.warning(`${hechos.length} actualizados, ${fallos} fallaron`)
    return
  }
  const verbo = accion === 'poner' ? 'puesta a' : 'quitada a'
  const clientes = hechos.length === 1 ? '1 cliente' : `${hechos.length} clientes`
  const tenian = sinCambio === 1 ? 'tenía' : 'tenían'
  const resto = accion === 'poner' ? `ya la ${tenian}` : `no la ${tenian}`
  const detalle = sinCambio > 0 ? ` ${sinCambio} ${resto}.` : ''
  toast.success(`Etiqueta "${nombre}" ${verbo} ${clientes}.${detalle}`)
}

export function Clientes() {
  // Hook para obtener la organización actual y usuario (multi-tenancy)
  const { organizacionId, usuario } = useOrganizacion()
  const { supabase } = useSupabase()
  const { puede, permisosListos, emailUsuario, rolMultiTenant } = usePermissions()
  const { transferirClavesInscripcion } = useVault()
  const queryClient = useQueryClient()
  // El estado de cuenta es la cuenta corriente: adjuntarlo pide además verMovimientos, como la pestaña
  // Cuenta de la ficha (ORR-613).
  const puedeAdjuntarEstadoCuenta = puede.enviarEstadoCuenta && puede.verMovimientos
  const { data: configEstudio } = useConfiguracionEstudio()
  // El contrato lleva la identidad del estudio: mientras esos datos no estén, no se puede enviar
  // (ORR-310). Cubre las dos razones por las que pueden faltar: el query todavía no resolvió (o
  // falló) y la organización nunca los cargó.
  const faltanDatosEstudio =
    !configEstudio?.nombre?.trim() ||
    !configEstudio?.responsable_nombre?.trim() ||
    !configEstudio?.direccion?.trim()
  const { getAccessTokenSilently } = useAuth0()

  // Hook para leer query params de la URL
  const [searchParams, setSearchParams] = useSearchParams()

  // Estado para filtros (unificado)
  const [filtros, setFiltros] = useState<FiltrosState>(FILTROS_INICIALES)

  // Tab activa para clasificación de clientes
  type CategoriaCliente = 'mensual' | 'esporadico' | 'inactivo'
  const [tabActiva, setTabActiva] = useState<CategoriaCliente>('mensual')

  // Estado para dialogs
  const [dialogOpen, setDialogOpen] = useState(false)
  const [clienteEditarId, setClienteEditarId] = useState<string | null>(null)
  const [clienteEliminar, setClienteEliminar] = useState<VistaSaldoClienteConImpuestos | null>(null)
  const [clienteEstadoCuenta, setClienteEstadoCuenta] = useState<VistaSaldoClienteConImpuestos | null>(null)
  const [clienteDetalle, setClienteDetalle] = useState<VistaSaldoClienteConImpuestos | null>(null)
  const [clienteDetalleTab, setClienteDetalleTab] = useState<string | undefined>(undefined)
  // ORR-233: releer el padrón de ARCA para varios clientes de una (recategorización).
  const [sincronizarMasivoOpen, setSincronizarMasivoOpen] = useState(false)
  const [clienteEnviarContrato, setClienteEnviarContrato] = useState<VistaSaldoClienteConImpuestos | null>(null)
  // ORR-327: id del cliente para el que se está cargando una tarea (null = dialog cerrado).
  // Guarda el id y no el objeto: los dos listados que disparan esto usan tipos de vista
  // distintos y solo hace falta el id para preasignar la tarea.
  const [clienteTareaId, setClienteTareaId] = useState<string | null>(null)
  const [enviandoContrato, setEnviandoContrato] = useState(false)
  // Si «Enviar contrato» adjunta el estado de cuenta. Arranca tildado cada vez que se abre, como en el alta.
  const [adjuntarEstadoCuentaContrato, setAdjuntarEstadoCuentaContrato] = useState(true)
  // ORR-404: el cliente que está por darse de baja, mientras el diálogo pide el motivo.
  // El cliente recién dado de alta, mientras el diálogo pregunta si se le manda la bienvenida.
  const [bienvenidaPendiente, setBienvenidaPendiente] = useState<{ clienteId: string; email: string } | null>(null)
  // La tarea de inscripción de la que sale el alta en curso, con su CUIT. Null en un alta común.
  const [inscripcionAlta, setInscripcionAlta] = useState<{ tareaId: string; cuit: string; asignadoA: string | null } | null>(null)
  const [inscripcionOpen, setInscripcionOpen] = useState(false)
  const [clienteParaBaja, setClienteParaBaja] = useState<VistaSaldoClienteConImpuestos | null>(null)

  // Estado de selección múltiple
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isAssigning, setIsAssigning] = useState(false)

  // Queries y mutations
  // Cargar todos los clientes — los tabs clasifican frontend
  // isPending y no isLoading: mientras la query espera a los permisos no corre, y una lista vacía
  // en ese momento no quiere decir que no haya clientes.
  const {
    data: clientes = [],
    isPending: isPendingClientes,
    error,
    refetch,
  } = useClientes()

  const altaCliente = useAltaCliente()
  const actualizarCliente = useActualizarCliente()
  const eliminarCliente = useEliminarCliente()
  const crearTarea = useCrearTarea()
  const asignarResponsablesMasivo = useAsignarResponsablesMasivo()
  const etiquetarEnLote = useEtiquetarEnLote()
  const quitarEtiquetaEnLote = useQuitarEtiquetaEnLote()

  // Catálogo de impuestos (para nombres reales en filtros)
  const { catalogo: catalogoImpuestos } = useCatalogoImpuestos()

  // Obtener servicios disponibles y servicios del cliente en edición
  const { data: serviciosDisponibles = [] } = useServicios()
  const { data: serviciosClienteActual = [] } = useServiciosCliente(clienteEditarId || '')

  // Catálogo de etiquetas de la org, etiquetas del cliente en edición (para precargar el wizard
  // y para el diff al guardar) y el mapa cliente→etiquetas para columna y filtro.
  const { data: catalogoEtiquetas = [] } = useEtiquetasCliente()
  const {
    data: etiquetasClienteActual = [],
    isLoading: isLoadingEtiquetasCliente,
    isError: errorEtiquetasCliente,
  } = useEtiquetasDeCliente(clienteEditarId || undefined)
  const { data: etiquetasPorCliente, isError: errorEtiquetasPorCliente } = useEtiquetasDeClientes()
  const guardarEtiquetas = useGuardarEtiquetasCliente()

  // Si esto falla, el filtro y la columna de etiquetas se ven vacíos sin ningún aviso: se leen
  // como "ningún cliente tiene esa etiqueta" en vez de como el error que son.
  useEffect(() => {
    if (errorEtiquetasPorCliente) {
      toast.error('No se pudieron cargar las etiquetas de los clientes')
    }
  }, [errorEtiquetasPorCliente])

  // Cargar cliente completo cuando se va a editar. Con un cliente en edición, el wizard solo se arma
  // sobre su ficha: sin ella quedaría en modo alta y el guardado pisaría el cliente con valores vacíos.
  // isPending y no isLoading: mientras espera a los permisos la query no corre.
  const fichaEditarQuery = useCliente(clienteEditarId || '')
  const fichaEditar =
    clienteEditarId && fichaEditarQuery.data?.id === clienteEditarId ? fichaEditarQuery.data : undefined
  const isLoadingClienteEditar = !!clienteEditarId && !fichaEditar && fichaEditarQuery.isPending
  const errorClienteEditar = !!clienteEditarId && !fichaEditar && !fichaEditarQuery.isPending

  // Procesar query params de la URL (desde búsqueda global)
  useEffect(() => {
    if (isPendingClientes) return

    const clienteId = searchParams.get('id')
    const editar = searchParams.get('editar')
    const estadoCuenta = searchParams.get('estado-cuenta')
    const ficha = searchParams.get('ficha')

    if (!clienteId) return

    // Buscar el cliente en la lista
    const cliente = clientes.find((c) => c.id === clienteId)

    if (!cliente) {
      // La lista del asistente solo trae los clientes de los que es responsable.
      toast.error('La ficha no se puede abrir porque ese cliente no está en tu lista de clientes.')
      setSearchParams({}, { replace: true })
      return
    }

    // Abrir el dialog correspondiente. Sin verMovimientos el estado de cuenta no se abre y va a la ficha.
    if (estadoCuenta === 'true' && puede.verMovimientos) {
      setClienteEstadoCuenta(cliente)
    } else if (editar === 'true') {
      setClienteEditarId(clienteId)
      setDialogOpen(true)
    } else {
      // Por defecto, ver detalle (ficha=cuenta linkea al tab Cuenta corriente — ORR-141)
      if (ficha) setClienteDetalleTab(ficha)
      setClienteDetalle(cliente)
    }

    // Limpiar los params de la URL para evitar reabrir al recargar
    setSearchParams({}, { replace: true })
  }, [clientes, isPendingClientes, searchParams, setSearchParams, puede.verMovimientos])

  // Vincula una inscripción con su cliente y pasa su clave fiscal a la Caja Fuerte del cliente.
  // Devuelve false si no pudo vincular, y el aviso lo da quien llama. Si la clave no pasa, avisa acá:
  // el detalle de la tarea ofrece pasarla de nuevo.
  const vincularInscripcion = async (tareaId: string, clienteId: string, asignadoA: string | null) => {
    try {
      if (!(await tareasService.vincularClienteDeInscripcion(supabase!, tareaId, clienteId))) return false
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
    } catch (err) {
      console.error('Error al vincular la inscripción con el cliente:', err)
      return false
    }
    if (!puedeVerClaveInscripcion(rolMultiTenant, asignadoA, emailUsuario)) return true
    try {
      if ((await transferirClavesInscripcion(tareaId, clienteId)) > 0) {
        toast.success('La clave fiscal de la inscripción pasó a la Caja Fuerte del cliente.')
      }
    } catch (err) {
      console.error('Error al pasar la clave fiscal a la Caja Fuerte:', err)
      toast.warning('La clave fiscal de la inscripción no se pudo pasar a la Caja Fuerte del cliente y se pasa desde el detalle de la tarea.')
    }
    return true
  }

  // Lee la inscripción de la que sale un alta. Devuelve null y avisa si la tarea no se pudo leer,
  // no es una inscripción, ya tiene cliente o el usuario no puede tocarla. Si ya hay un cliente con
  // ese CUIT, el alta no se abre: la inscripción se vincula con él. Así se completa un alta anterior
  // que creó el cliente y no llegó a vincular la tarea. Un cliente inactivo no se vincula: la tarea
  // queda pendiente hasta que alguien lo reactive.
  const leerInscripcionParaAlta = async (tareaId: string) => {
    try {
      const tarea = await tareasService.obtenerPorId(supabase!, tareaId)
      if (tarea.tipo !== 'inscripcion' || !tarea.cuit) {
        toast.error('El cliente no se puede dar de alta desde esta tarea porque no es una inscripción.')
        return null
      }
      if (tarea.cliente_id) {
        toast.error('El cliente no se puede dar de alta desde esta inscripción porque ya tiene un cliente vinculado.')
        return null
      }
      if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
        toast.error('El cliente no se puede dar de alta desde esta inscripción porque la tarea está asignada a otra persona.')
        return null
      }
      const { clienteExistente } = await personasService.verificarCUITDisponible(supabase!, tarea.cuit, organizacionId!)
      if (clienteExistente) {
        if ((await clientesService.obtenerEstado(supabase!, clienteExistente.id)) !== 'activo') {
          // Reactivar pide editarCliente: sin él, la salida es otra persona.
          if (!puede.editarCliente) {
            toast.warning(
              `El CUIT es de ${clienteExistente.nombre}, que está inactivo. Pedile a un administrador que lo reactive; después, «Dar de alta el cliente» en la tarea la vincula con él.`,
            )
            return null
          }
          toast.warning(
            `El CUIT es de ${clienteExistente.nombre}, que está inactivo. Reactivalo y volvé a la tarea: «Dar de alta el cliente» la vincula con él.`,
            { action: { label: 'Ver ficha', onClick: () => setSearchParams({ id: clienteExistente.id }) } },
          )
          return null
        }
        if (await vincularInscripcion(tareaId, clienteExistente.id, tarea.asignado_a)) {
          toast.success(`El CUIT ya era de ${clienteExistente.nombre}: la inscripción quedó vinculada con ese cliente.`)
        } else {
          toast.error(`El CUIT ya es de ${clienteExistente.nombre}, pero la inscripción no se pudo vincular con ese cliente.`)
        }
        return null
      }
      // La búsqueda de arriba respeta la cartera: un cliente que el usuario no ve figura disponible,
      // y el alta chocaría al guardar con el control de CUIT duplicado.
      if (await personasService.existeClienteConCuit(supabase!, tarea.cuit, organizacionId!)) {
        toast.warning(
          'El CUIT ya es de un cliente que no tenés asignado. Pedile a un administrador que vincule la inscripción desde la tarea.',
        )
        return null
      }
      return { tareaId, cuit: tarea.cuit, asignadoA: tarea.asignado_a }
    } catch (err) {
      console.error('Error al leer la inscripción:', err)
      toast.error('El cliente no se puede dar de alta desde la inscripción porque no se pudo leer la tarea o verificar su CUIT.')
      return null
    }
  }

  // `?nuevo=true` abre el alta de cliente si hay permiso de crear, y se limpia de la URL. Con
  // `inscripcion=<id>`, el alta arranca con el CUIT de esa tarea de inscripción.
  const abrirAltaDesdeUrl = useEffectEvent(async (inscripcionId: string | null) => {
    if (!puede.crearCliente) return
    setClienteEditarId(null)
    setInscripcionAlta(null)
    if (inscripcionId) {
      const inscripcion = await leerInscripcionParaAlta(inscripcionId)
      if (!inscripcion) return
      setInscripcionAlta(inscripcion)
    }
    setDialogOpen(true)
  })
  useEffect(() => {
    if (searchParams.get('nuevo') !== 'true' || !supabase) return
    void abrirAltaDesdeUrl(searchParams.get('inscripcion'))
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams, supabase])

  // El CUIT de la inscripción con el que arranca el alta. Memorizado: el wizard toma sus valores
  // iniciales una sola vez.
  const valoresAltaInscripcion = useMemo(
    () => (inscripcionAlta ? { cuit: formatearCUIT(inscripcionAlta.cuit) } : undefined),
    [inscripcionAlta],
  )

  // Filtrado frontend (instantáneo)
  const clientesFiltrados = useMemo(() => {
    return clientes.filter((cliente) => {
      // Búsqueda de texto (incluye código de cliente)
      if (filtros.busqueda) {
        const termino = filtros.busqueda.toLowerCase()
        const codigoCliente = cliente.numero_cliente?.toString().padStart(3, '0') || ''
        const matchBusqueda =
          cliente.nombre?.toLowerCase().includes(termino) ||
          cliente.cuit?.toString().includes(termino) ||
          cliente.email?.toLowerCase().includes(termino) ||
          cliente.responsable?.toLowerCase().includes(termino) ||
          codigoCliente.includes(termino) ||
          `cli-${codigoCliente}`.includes(termino)
        if (!matchBusqueda) return false
      }

      // Tipo de Persona (OR)
      if (filtros.tipoPersona.length > 0) {
        if (!filtros.tipoPersona.includes(cliente.tipo_persona ?? '')) return false
      }

      // Responsable (OR) — a client matches if ANY of its responsables is in the filter
      if (filtros.responsable.length > 0) {
        const responsablesCliente = cliente.responsable?.split(', ') || []
        if (!filtros.responsable.some((f: string) => responsablesCliente.includes(f))) return false
      }

      // Condicion Fiscal (OR)
      if (filtros.condicionFiscal.length > 0) {
        if (!filtros.condicionFiscal.includes(cliente.condicion_iva ?? '')) return false
      }

      // Provincia (OR)
      if (filtros.provincia.length > 0) {
        if (!filtros.provincia.includes(cliente.provincia ?? '')) return false
      }

      // Impuestos (lógica AND: cliente debe tener TODOS los seleccionados)
      if (filtros.impuestos.length > 0) {
        const impuestosCliente = cliente.impuestos_liquidar?.map((i) => i.impuesto_id) || []
        const tieneTodos = filtros.impuestos.every((id) => impuestosCliente.includes(id))
        if (!tieneTodos) return false
      }

      // Etiquetas (OR, igual que tipo/responsable/provincia): alcanza con una coincidencia
      if (filtros.etiqueta.length > 0) {
        const etiquetasCliente = (cliente.id && etiquetasPorCliente?.get(cliente.id)) || []
        if (!filtros.etiqueta.some((id) => etiquetasCliente.includes(id))) return false
      }

      return true
    })
  }, [clientes, filtros, etiquetasPorCliente])

  // Clasificar clientes por tab y calcular conteos
  const clasificarCliente = (cliente: VistaSaldoClienteConImpuestos): CategoriaCliente => {
    if (cliente.estado !== 'activo') return 'inactivo'
    return cliente.servicios_activos?.some((s) => s.frecuencia === 'mensual')
      ? 'mensual'
      : 'esporadico'
  }

  const { clientesPorTab, conteos } = useMemo(() => {
    const grupos: Record<CategoriaCliente, VistaSaldoClienteConImpuestos[]> = {
      mensual: [],
      esporadico: [],
      inactivo: [],
    }
    for (const c of clientesFiltrados) {
      grupos[clasificarCliente(c)].push(c)
    }
    return {
      clientesPorTab: grupos,
      conteos: {
        mensual: grupos.mensual.length,
        esporadico: grupos.esporadico.length,
        inactivo: grupos.inactivo.length,
      },
    }
  }, [clientesFiltrados])

  // ORR-128: el scoping del asistente ahora es server-side (useClientes filtra por responsable),
  // así que la lista visible es directamente la del tab activo.
  const clientesVisibles = clientesPorTab[tabActiva]

  // Datos para filtros dinámicos
  const responsablesDisponibles = useMemo(() => {
    return [...new Set(clientes.flatMap((c) =>
      c.responsable ? c.responsable.split(', ') : []
    ))] as string[]
  }, [clientes])

  const provinciasDisponibles = useMemo(() => {
    return [...new Set(clientes.map((c) => c.provincia).filter(Boolean))].sort() as string[]
  }, [clientes])

  const impuestosDisponibles = useMemo(() => {
    const idsUsados = new Set<string>()
    clientes.forEach((cliente) => {
      cliente.impuestos_liquidar?.forEach((imp) => {
        idsUsados.add(imp.impuesto_id)
      })
    })
    return catalogoImpuestos
      .filter((ci) => idsUsados.has(ci.id))
      .map((ci) => ({
        id: ci.id,
        codigo: ci.codigo,
        nombre: ci.nombre,
      }))
      .sort((a, b) => (a.nombre ?? '').localeCompare(b.nombre ?? ''))
  }, [clientes, catalogoImpuestos])

  // Datos de último envío de estado de cuenta (key por cliente_id para incluir
  // clientes sin email — ver ORR-109).
  const clienteIds = useMemo(() =>
    (clientes ?? []).map(c => c.id).filter((id): id is string => id !== null),
    [clientes]
  )
  const { data: ultimosEnvios } = useUltimosEnviosEstadoCuenta(clienteIds, organizacionId ?? undefined)

  // Handlers
  const handleNuevoCliente = () => {
    setClienteEditarId(null)
    setDialogOpen(true)
  }

  // Handlers de selección múltiple
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev)
    setSelectedIds(new Set())
  }, [])

  const handleSelectionChange = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(clientesVisibles.map((c) => c.id).filter(Boolean) as string[]))
  }, [clientesVisibles])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setSelectionMode(false)
  }, [])

  const handleAsignarResponsable = useCallback(async (usuarioId: string) => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    setIsAssigning(true)
    try {
      const result = await asignarResponsablesMasivo.mutateAsync({ clienteIds: ids, usuarioIds: [usuarioId] })
      if (result.errors === 0) {
        toast.success(`Responsable asignado a ${result.success} clientes`)
      } else {
        toast.warning(`${result.success} asignados, ${result.errors} fallaron`)
      }
      setSelectedIds(new Set())
      setSelectionMode(false)
    } catch {
      toast.error('Error al asignar responsable')
    } finally {
      setIsAssigning(false)
    }
  }, [selectedIds, asignarResponsablesMasivo])

  const handleQuitarResponsable = useCallback(async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    setIsAssigning(true)
    try {
      const result = await asignarResponsablesMasivo.mutateAsync({ clienteIds: ids, usuarioIds: [] })
      if (result.errors === 0) {
        toast.success(`Responsable quitado de ${result.success} clientes`)
      } else {
        toast.warning(`${result.success} actualizados, ${result.errors} fallaron`)
      }
      setSelectedIds(new Set())
      setSelectionMode(false)
    } catch {
      toast.error('Error al quitar responsable')
    } finally {
      setIsAssigning(false)
    }
  }, [selectedIds, asignarResponsablesMasivo])

  const handleEtiquetaEnLote = useCallback(
    async (accion: AccionEtiqueta, etiqueta: { id: string; nombre: string }) => {
      const ids = Array.from(selectedIds)
      if (ids.length === 0) return
      setIsAssigning(true)
      try {
        const mutacion = accion === 'poner' ? etiquetarEnLote : quitarEtiquetaEnLote
        const resultado = await mutacion.mutateAsync({ clienteIds: ids, etiqueta })
        avisarEtiquetaEnLote(accion, etiqueta.nombre, resultado)
        setSelectedIds(new Set())
        setSelectionMode(false)
      } catch {
        // El hook de la mutación muestra el error.
      } finally {
        setIsAssigning(false)
      }
    },
    [selectedIds, etiquetarEnLote, quitarEtiquetaEnLote]
  )

  const handleEditarCliente =(cliente: VistaSaldoClienteConImpuestos) => {
    setClienteEditarId(cliente.id)
    setDialogOpen(true)
  }

  const handleEliminarCliente = (cliente: VistaSaldoClienteConImpuestos) => {
    setClienteEliminar(cliente)
  }

  // El guardado abarca el cliente, los servicios y las etiquetas. Mientras dura, el wizard queda
  // montado y deshabilitado, y un submit nuevo se ignora.
  const [guardandoCliente, setGuardandoCliente] = useState(false)
  const guardandoClienteRef = useRef(false)

  const handleSubmit = async (data: ClienteFormValues, contexto?: ContextoEdicionCliente) => {
    if (guardandoClienteRef.current) return
    guardandoClienteRef.current = true
    setGuardandoCliente(true)
    try {
      if (clienteEditarId) {
        // El diálogo ya no arma el wizard sin la ficha; esto cubre cualquier otro camino al guardado.
        if (fichaEditar?.id !== clienteEditarId) {
          toast.error('No se guardó: la ficha del cliente no está cargada')
          return
        }
        const clienteData = armarClienteInput(data, { organizacionId, createdBy: usuario?.email || null, puede })
        await actualizarCliente.mutateAsync({
          id: clienteEditarId,
          data: clienteData,
        })
        toast.success('Cliente actualizado correctamente')

        // Guardar servicios con precios, como diferencia contra los que el formulario precargó. Sin
        // permiso de ver y de editar honorarios no se tocan, y tampoco si el formulario nunca los leyó.
        if (supabase && puede.editarHonorariosCliente && puede.verHonorariosCliente) {
          if (!contexto?.serviciosBase) {
            toast.error('No se guardaron los servicios: no se pudieron leer los del cliente')
          } else {
            let resultado: ResultadoGuardarServicios
            try {
              resultado = await guardarServiciosCliente(
                supabase,
                clienteEditarId,
                contexto.serviciosBase,
                serviciosConPrecioValido(data.serviciosConPrecio),
              )
            } catch (err) {
              // La RPC no escribió nada. El diálogo queda abierto para reintentar con lo cargado.
              console.error(err)
              toast.error('Se guardaron los datos del cliente, pero no los servicios. Volvé a intentarlo.')
              return
            }
            if (resultado.conflicto) {
              toast.error('No se guardaron los servicios: otro usuario los cambió mientras editabas', {
                description: 'Volvé a abrir la edición para ver los servicios actuales.',
              })
            }
            queryClient.invalidateQueries({ queryKey: serviciosKeys.cliente(clienteEditarId) })
          }
        }

        await guardarEtiquetas({
          clienteId: clienteEditarId,
          idsActuales: etiquetasClienteActual,
          idsFinal: data.etiquetas ?? [],
          clienteNombre: clienteData.nombre,
        })
      } else {
        const { clienteId, input } = await altaCliente.darDeAlta(data)
        toast.success('Cliente creado correctamente')
        if (inscripcionAlta) {
          // El CUIT precargado se puede cambiar en el formulario. Con otro CUIT, el cliente no es el
          // de la inscripción y la tarea queda sin vincular.
          if (String(input.cuit ?? '').replace(/\D/g, '') !== inscripcionAlta.cuit) {
            toast.warning('El cliente se creó con otro CUIT, así que la tarea de inscripción quedó sin vincular.')
          } else if (!(await vincularInscripcion(inscripcionAlta.tareaId, clienteId, inscripcionAlta.asignadoA))) {
            toast.warning('El cliente se creó, pero la tarea de inscripción no se pudo vincular con él.')
          }
          setInscripcionAlta(null)
        }
        // Sin email no hay a quién preguntarle: `enviarBienvenida` solo avisa que no se envió.
        if (input.email) setBienvenidaPendiente({ clienteId, email: input.email })
        else void altaCliente.enviarBienvenida(clienteId, input.email)
      }

      setDialogOpen(false)
      setClienteEditarId(null)
    } catch (err) {
      toast.error(
        clienteEditarId
          ? 'Error al actualizar el cliente'
          : 'Error al crear el cliente'
      )
      console.error(err)
    } finally {
      guardandoClienteRef.current = false
      setGuardandoCliente(false)
    }
  }

  // Handler para enviar contrato desde el menu de acciones
  const handleEnviarContrato = (cliente: VistaSaldoClienteConImpuestos) => {
    setAdjuntarEstadoCuentaContrato(true)
    setClienteEnviarContrato(cliente)
  }

  const handleConfirmarEnvioContrato = async (desactivarAnteriores: boolean) => {
    if (!clienteEnviarContrato?.id) return
    if (!supabase) {
      toast.error('Supabase no disponible')
      return
    }

    setEnviandoContrato(true)
    try {
      // Generar en el navegador los PDF del contrato y, si está tildado, del estado de cuenta (base64).
      // Si el estado de cuenta falla, el contrato sale igual sin él (null) y el toast lo avisa.
      const adjuntarEstadoCuenta = adjuntarEstadoCuentaContrato && puedeAdjuntarEstadoCuenta
      // El estado de cuenta se lee dentro de la organización activa. Que falte es un estado de la
      // sesión, no una falla del PDF: el envío entero cae en el aviso de error del catch.
      if (adjuntarEstadoCuenta && !organizacionId) throw new Error('No organization')
      const [contratoBase64, estadoCuentaBase64] = await Promise.all([
        generarContratoBase64({
          supabase,
          clienteId: clienteEnviarContrato.id,
          configEstudio: configEstudio ?? null,
          puedeVerHonorarios: puede.verHonorariosCliente,
        }),
        adjuntarEstadoCuenta && organizacionId
          ? generarEstadoCuentaBase64({
              supabase,
              clienteId: clienteEnviarContrato.id,
              organizacionId,
              configEstudio: configEstudio ?? null,
            }).catch((error: unknown) => {
              console.error('Error generando el estado de cuenta:', error)
              return null
            })
          : undefined,
      ])

      const token = await getAccessTokenSilently()

      const response = await fetch('/.netlify/functions/enviar-bienvenida-cliente', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cliente_id: clienteEnviarContrato.id,
          tipo_envio: 'reenvio',
          desactivar_anteriores: desactivarAnteriores,
          contrato_base64: contratoBase64,
          estado_cuenta_base64: estadoCuentaBase64 ?? undefined,
        }),
      })

      if (!response.ok) {
        throw new Error('Error al enviar el contrato')
      }

      if (estadoCuentaBase64 === null) {
        toast.warning('Contrato enviado sin el estado de cuenta', {
          description: `Se envió a ${clienteEnviarContrato.email}. El estado de cuenta no se pudo generar: mandalo desde su diálogo.`,
        })
      } else {
        toast.success('Contrato enviado', {
          description: `Se envió a ${clienteEnviarContrato.email}`,
        })
      }
      setClienteEnviarContrato(null)
    } catch (error) {
      console.error('Error enviando contrato:', error)
      if (error instanceof DatosEstudioIncompletosError) {
        toast.error('No se puede generar el contrato', { description: error.message })
      } else {
        toast.error('Error al enviar el contrato')
      }
    } finally {
      setEnviandoContrato(false)
    }
  }

  const handleConfirmarEliminar = async () => {
    if (!clienteEliminar?.id) return
    try {
      await eliminarCliente.mutateAsync(clienteEliminar.id)
      toast.success('Cliente eliminado correctamente')
      setClienteEliminar(null)
    } catch (err) {
      handleMutationError(err, 'Error al eliminar el cliente')
    }
  }

  const handleVerDetalle = (cliente: VistaSaldoClienteConImpuestos) => {
    setClienteDetalle(cliente)
  }

  const handleVerEstadoCuenta = (cliente: VistaSaldoClienteConImpuestos) => {
    setClienteEstadoCuenta(cliente)
  }

  // ORR-327: alta rápida con el cliente de origen ya asignado. Sin adjuntos ni repetición
  // a propósito —los dos viven en la página de Tareas, que es la que sabe procesarlos—:
  // ofrecerlos acá los aceptaría en el form y los descartaría en el submit.
  const handleCargarTarea = useCallback((cliente: { id: string | null }) => {
    if (cliente.id) setClienteTareaId(cliente.id)
  }, [])

  const handleCrearTarea = async (data: TareaFormValues) => {
    // `TareaFormDialog` no espera esta promesa, así que un rechazo acá quedaría sin manejar
    // y el dialog se cerraría igual, perdiendo lo cargado. El toast de error ya lo emite el
    // `onError` de `useCrearTarea` (mismo patrón que Calendario.tsx).
    try {
      await crearTarea.mutateAsync(data)
      toast.success('Tarea creada correctamente')
      setClienteTareaId(null)
    } catch (error) {
      console.error('Error al crear tarea desde clientes:', error)
    }
  }

  // ORR-404: dar de baja pide el motivo primero; reactivar sigue siendo un click.
  const handleCambiarEstado = (cliente: VistaSaldoClienteConImpuestos, nuevoEstado: 'Activo' | 'Inactivo') => {
    if (!cliente.id) return
    if (nuevoEstado === 'Inactivo') {
      setClienteParaBaja(cliente)
      return
    }
    void aplicarCambioEstado(cliente, 'Activo')
  }

  const aplicarCambioEstado = async (
    cliente: VistaSaldoClienteConImpuestos,
    nuevoEstado: 'Activo' | 'Inactivo',
    motivo?: MotivoBajaFormValues,
  ): Promise<boolean> => {
    if (!cliente.id) return false
    const clienteId = cliente.id
    // estado guardado en DB ya viene en minúsculas (union de ClienteCompletoInput)
    const estadoAnterior = (cliente.estado ?? undefined) as ClienteCompletoInput['estado']
    const estadoNuevo = nuevoEstado.toLowerCase() as 'activo' | 'inactivo'
    const accion = nuevoEstado === 'Activo' ? 'reactivado' : 'dado de baja'

    try {
      // Cambiar estado inmediatamente. actualizarCompleto acepta input parcial
      // (solo aplica los campos definidos); el cambio de estado no toca persona/faceta.
      // El "Deshacer" de abajo no manda el motivo en null: la base lo limpia al reactivar.
      await actualizarCliente.mutateAsync({
        id: clienteId,
        data: {
          estado: estadoNuevo,
          ...(motivo
            ? { motivo_baja: motivo.motivo, motivo_baja_detalle: motivo.detalle ?? null }
            : {}),
        },
      })

      // Mostrar toast con opción de deshacer
      toast.success(`Cliente ${accion}`, {
        description: cliente.nombre ?? undefined,
        action: {
          label: 'Deshacer',
          onClick: async () => {
            try {
              await actualizarCliente.mutateAsync({
                id: clienteId,
                data: { estado: estadoAnterior },
              })
              toast.success('Cambio deshecho')
            } catch (err) {
              toast.error('Error al deshacer el cambio')
              console.error(err)
            }
          },
        },
      })
      return true
    } catch (err) {
      toast.error('Error al cambiar el estado del cliente')
      console.error(err)
      return false
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Clientes"
        description="Gestion de clientes del estudio"
      />

      <div className="flex-1 flex flex-col p-4 md:p-6 gap-4 md:gap-6 min-h-0">
        {/* Barra de filtros */}
        <div className="shrink-0 flex flex-col gap-3 md:gap-4">
          <div className="flex flex-col lg:flex-row gap-3 md:gap-4 justify-between">
            <ClientesFiltros
              filtros={filtros}
              onChange={setFiltros}
              responsablesDisponibles={responsablesDisponibles}
              provinciasDisponibles={provinciasDisponibles}
              impuestosDisponibles={impuestosDisponibles}
              etiquetasDisponibles={catalogoEtiquetas}
              className="flex-1"
            />

            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={!permisosListos}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              {(puede.asignarResponsablesCliente || puede.editarCliente) && (
                <Button
                  variant={selectionMode ? 'secondary' : 'outline'}
                  size="icon"
                  onClick={toggleSelectionMode}
                  className="h-9 w-9 hidden sm:flex"
                  title={selectionMode ? 'Cancelar selección' : 'Seleccionar múltiples'}
                >
                  {selectionMode ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </Button>
              )}
              {puede.sincronizarPadron && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSincronizarMasivoOpen(true)}
                  className="h-9 w-9 hidden sm:flex"
                  title="Actualizar categorías y actividades desde ARCA"
                >
                  <CloudDownload className="h-4 w-4" />
                </Button>
              )}
              {puede.crearTarea && (
                <Button
                  variant="outline"
                  size="sm"
                  className="sm:size-default"
                  onClick={() => setInscripcionOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Nueva</span> inscripción
                </Button>
              )}
              {puede.crearCliente && (
                <Button onClick={handleNuevoCliente} size="sm" className="sm:size-default">
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Nuevo</span> Cliente
                </Button>
              )}
            </div>
          </div>

          {/* Tabs de clasificación */}
          <Tabs value={tabActiva} onValueChange={(v) => setTabActiva(v as CategoriaCliente)}>
            <TabsList>
              <TabsTrigger value="mensual" className="gap-1.5">
                <UserCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Mensuales</span>
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {conteos.mensual}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="esporadico" className="gap-1.5">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Esporádicos</span>
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {conteos.esporadico}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="inactivo" className="gap-1.5">
                <UserX className="h-4 w-4" />
                <span className="hidden sm:inline">Inactivos</span>
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {conteos.inactivo}
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Barra de acciones bulk */}
        {selectionMode && selectedIds.size > 0 && (
          <ClientesBulkActionsBar
            selectedCount={selectedIds.size}
            totalCount={clientesVisibles.length}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onAsignarResponsable={handleAsignarResponsable}
            onQuitarResponsable={handleQuitarResponsable}
            onEtiquetar={(etiqueta) => handleEtiquetaEnLote('poner', etiqueta)}
            onQuitarEtiqueta={(etiqueta) => handleEtiquetaEnLote('quitar', etiqueta)}
            mostrarResponsables={puede.asignarResponsablesCliente}
            mostrarEtiquetas={puede.editarCliente}
            isAssigning={isAssigning}
          />
        )}

        {/* Contenedor de tabla con scroll interno */}
        <div className="flex-1 min-h-0 flex flex-col">
          {error ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-destructive">
                Error al cargar los clientes. Verifica la conexión con Supabase.
              </p>
            </div>
          ) : (
            <ClientesTable
              clientes={clientesVisibles}
              isLoading={isPendingClientes}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
              onEditar={handleEditarCliente}
              onEliminar={handleEliminarCliente}
              onVerDetalle={handleVerDetalle}
              onVerEstadoCuenta={handleVerEstadoCuenta}
              onCambiarEstado={handleCambiarEstado}
              onEnviarContrato={handleEnviarContrato}
              ultimosEnvios={ultimosEnvios}
              onRowClick={handleVerDetalle}
              onCargarTarea={handleCargarTarea}
              canEdit={puede.editarCliente}
              canDelete={puede.eliminarCliente}
              canCreateTarea={puede.crearTarea}
              mostrarMontos={puede.verHonorariosCliente}
              mostrarEstadoCuenta={puede.verMovimientos}
            />
          )}

        </div>
      </div>

      {/* Dialog para crear/editar cliente */}
      <ClienteFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) {
            setClienteEditarId(null)
            setInscripcionAlta(null)
          }
        }}
        cliente={fichaEditar ?? null}
        esEdicion={!!clienteEditarId}
        errorCarga={errorClienteEditar}
        onReintentar={() => fichaEditarQuery.refetch()}
        servicios={serviciosDisponibles}
        serviciosSeleccionados={serviciosClienteActual}
        etiquetasSeleccionadas={etiquetasClienteActual}
        // Sin las etiquetas actuales del cliente, el selector no puede mostrar ni reconciliar
        // el conjunto real: se oculta y el guardado no toca las etiquetas.
        mostrarEtiquetas={!errorEtiquetasCliente}
        onSubmit={handleSubmit}
        valoresIniciales={valoresAltaInscripcion}
        isLoading={isLoadingClienteEditar || isLoadingEtiquetasCliente}
        isSaving={guardandoCliente}
      />

      <InscripcionFormDialog open={inscripcionOpen} onOpenChange={setInscripcionOpen} />

      {bienvenidaPendiente && (
        <ConfirmarBienvenidaDialog
          open
          email={bienvenidaPendiente.email}
          onCancelar={() => setBienvenidaPendiente(null)}
          onConfirmar={(adjuntarEstadoCuenta) => {
            // No se espera: el envío avisa su resultado con su propio toast.
            void altaCliente.enviarBienvenida(bienvenidaPendiente.clienteId, bienvenidaPendiente.email, {
              adjuntarEstadoCuenta,
            })
            setBienvenidaPendiente(null)
          }}
        />
      )}

      {/* Dialog de confirmación para eliminar */}
      <AlertDialog
        open={!!clienteEliminar}
        onOpenChange={(open: boolean) => !open && setClienteEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el
              cliente <strong>{clienteEliminar?.nombre}</strong> y todos sus
              datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarCliente.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de estado de cuenta */}
      <EstadoCuentaDialog
        open={!!clienteEstadoCuenta}
        onOpenChange={(open) => !open && setClienteEstadoCuenta(null)}
        cliente={clienteEstadoCuenta}
        onVerFicha={() => {
          const c = clienteEstadoCuenta
          if (!c) return
          setClienteEstadoCuenta(null)
          setClienteDetalleTab('cuenta')
          setClienteDetalle(c)
        }}
      />

      {/* ORR-233: releer el padrón de ARCA en lote. Toma la lista completa de activos y
          no `clientesFiltrados`: el caso de uso es "todos los monotributistas del estudio"
          después de una recategorización, no lo que esté filtrado en pantalla. */}
      <SincronizarPadronMasivoDialog
        open={sincronizarMasivoOpen}
        onOpenChange={setSincronizarMasivoOpen}
        clientes={clientes
          .filter((c) => c.estado === 'activo')
          .map((c) => ({
            id: c.id as string,
            nombre: c.nombre ?? 'Sin nombre',
            cuit: c.cuit != null ? String(c.cuit) : null,
            esMonotributista: c.condicion_iva === 'monotributo',
          }))}
      />

      {/* Dialog de detalle de cliente */}
      <ClienteDetalleDialog
        open={!!clienteDetalle}
        onOpenChange={(open) => {
          if (!open) {
            setClienteDetalle(null)
            setClienteDetalleTab(undefined)
          }
        }}
        cliente={clienteDetalle as unknown as VistaSaldoCliente | null}
        initialTab={clienteDetalleTab}
        onEdit={(cliente) => {
          setClienteDetalle(null)
          setClienteEditarId(cliente.id)
          setDialogOpen(true)
        }}
        onEnviarEstadoCuenta={(cliente) => {
          setClienteDetalle(null)
          setClienteEstadoCuenta(cliente as unknown as VistaSaldoClienteConImpuestos)
        }}
        onCargarTarea={handleCargarTarea}
      />

      {/* ORR-327: alta de tarea con el cliente ya asignado, desde los 3 puntitos o la ficha */}
      <TareaFormDialog
        open={!!clienteTareaId}
        onOpenChange={(open) => !open && setClienteTareaId(null)}
        onSubmit={handleCrearTarea}
        isLoading={crearTarea.isPending}
        defaultValues={clienteTareaId ? { cliente_id: clienteTareaId } : undefined}
      />


      {/* ORR-404: el motivo se pide antes de dar de baja */}
      <MotivoBajaDialog
        open={!!clienteParaBaja}
        onOpenChange={(open) => {
          if (!open) setClienteParaBaja(null)
        }}
        nombreCliente={clienteParaBaja?.nombre}
        onConfirmar={async (values) => {
          if (!clienteParaBaja) return false
          return aplicarCambioEstado(clienteParaBaja, 'Inactivo', values)
        }}
      />

      {/* Dialog para enviar contrato desde menu de acciones */}
      <AlertDialog
        open={!!clienteEnviarContrato}
        onOpenChange={(open) => !open && setClienteEnviarContrato(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Enviar Contrato
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Se enviará el contrato de servicios a{' '}
                  <strong>{clienteEnviarContrato?.email}</strong>.
                </p>
                <p className="text-sm">
                  Si el cliente tiene contratos anteriores, puede elegir
                  desactivarlos o mantenerlos activos.
                </p>
                {faltanDatosEstudio && (
                  <p className="text-sm text-destructive">
                    No se puede enviar: faltan los datos del estudio (nombre, responsable y
                    domicilio) que van en el encabezado y la firma del contrato. Cargalos en
                    Configuración → General y volvé a intentar.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {puedeAdjuntarEstadoCuenta && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="contrato-adjuntar-estado-cuenta"
                checked={adjuntarEstadoCuentaContrato}
                onCheckedChange={(valor) => setAdjuntarEstadoCuentaContrato(valor === true)}
                disabled={enviandoContrato}
              />
              <Label htmlFor="contrato-adjuntar-estado-cuenta">Adjuntar estado de cuenta</Label>
            </div>
          )}
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={enviandoContrato}>Cancelar</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleConfirmarEnvioContrato(false)}
              disabled={enviandoContrato || faltanDatosEstudio}
            >
              Mantener Anteriores
            </Button>
            <Button
              onClick={() => handleConfirmarEnvioContrato(true)}
              disabled={enviandoContrato || faltanDatosEstudio}
            >
              {enviandoContrato ? 'Enviando...' : 'Desactivar Anteriores'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
